/**
 * Eine Tour eintragen.
 *
 * Bewusst „eintragen" und nicht „anlegen und disponieren": Es gibt hinterher
 * nichts zu drücken. Die Tour steht in der Liste, und ihr Zustand ergibt sich
 * aus dem, was passiert.
 *
 * Dateien können gleich mit. Der Auftrag des Kunden kommt als PDF oder Foto
 * herein, lange bevor jemand losfährt — und gehört an dieselbe Stelle wie die
 * Lieferscheine, die der Fahrer später fotografiert. Sonst liegt die eine
 * Hälfte im Backoffice und die andere im Mailpostfach.
 *
 * Die Dateien werden erst NACH dem Auftrag hochgeladen, weil sie eine
 * Auftragskennung brauchen. Scheitert der Upload, steht der Auftrag trotzdem —
 * und das ist richtig so: Eine Tour, die nur deshalb nicht existiert, weil ein
 * PDF zu groß war, wäre der schlechtere Fehler.
 *
 * ═══ DAS FORMULAR ÜBERLEBT DEN SEITENWECHSEL ═══
 *
 * Wer hier zwischendurch nachsehen will, wie ein Kunde heißt, kommt zurück und
 * findet seine Eingaben wieder. Der Entwurf liegt in `sessionStorage`; die
 * Begründung für genau diesen Speicher steht in `daten/entwurf.ts`.
 *
 * Ausgewählte Dateien überleben den Wechsel innerhalb des Backoffice im
 * Arbeitsspeicher der Seite (`fluechtig…` in `daten/entwurf.ts`), aber kein
 * Neuladen — dann nennt der Hinweis, welche noch einmal zu wählen sind.
 *
 * ═══ NACH DEM EINTRAGEN IST DAS FORMULAR WIRKLICH LEER (HJ-022) ═══
 *
 * Zurückgesetzt wurde vorher nur der Zustand DIESER Komponente. Die
 * Suchfelder behielten ihren getippten Text, das Dateifeld seine Auswahl —
 * im Büro standen danach „Korn“ und „Hei“ in Von/Nach und der Name der eben
 * hochgeladenen PDF. Jetzt zählt `runde` mit jeder eingetragenen Tour hoch
 * und steckt im `key` aller Felder mit eigenem Innenleben: React baut sie neu
 * auf, und nichts kann stehen bleiben, an das hier niemand gedacht hat.
 *
 * ═══ NUR DAS LADEDATUM BLEIBT STEHEN (HJ-040, E-052) ═══
 *
 * Bis dahin blieben auch Art, Fahrer, Fahrzeug, Preisart und Einheit stehen,
 * damit man sie bei fünf Touren desselben Tages nicht fünfmal setzt. Aus dem
 * Betrieb kam: Wer nicht aufpasst, übernimmt den Fahrer der vorigen Tour — und
 * eine falsch zugeordnete Tour ist schlimmer als ein paar Klicks mehr. Das
 * Ladedatum bleibt, weil oft mehrere Touren für denselben Tag erfasst werden.
 *
 * ═══ PENDELVERKEHR BRAUCHT RELATIONEN, NICHT EIN VON UND NACH ═══
 *
 * Eine Pendeltour fährt eine oder mehrere Strecken, und der Fahrer zählt die
 * Fuhren je Strecke. Das Formular bot bei „Pendelverkehr" nur ein optionales
 * Von und Nach — die Tour entstand OHNE Fuhrenblock, in der App stand „Noch
 * keine Relation erfasst", und es gab keinen Knopf, etwas hinzuzufügen. So ist
 * 26-0007 entstanden.
 *
 * Jetzt ersetzen die Relationen Von und Nach, und je Relation entsteht ein
 * Fuhrenblock mit Anzahl 0 — dasselbe Verfahren wie in der Disposition der App:
 * ANLEGEN IST PLANEN, gezählt wird beim Fahren. Am Auftrag stehen die Orte der
 * ERSTEN Relation, sonst zeigte die Disposition „— → —".
 *
 * ═══ VIER SCHRITTE, JEDER KANN SCHEITERN ═══
 *
 * Auftrag → Fahrt → Fuhrenblöcke → Dateien. Ab dem ersten steht die Tour, und
 * jeder spätere Fehler nimmt sie nicht zurück, sondern wird GEMELDET — mit dem
 * Weg zur Tour. Der Fehler, den das früher nicht war: Scheiterte die Fahrt,
 * ging es in denselben `catch` wie ein gescheiterter Auftrag, `angelegt` blieb
 * leer, der Knopf war wieder klickbar — und der zweite Klick legte die Tour ein
 * zweites Mal an, die erste stand als „Ohne Fahrt" in der Liste.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Fehlerkasten,
  Karte,
  Knopf,
  Meldung,
  Textfeld,
  Zahlfeld,
} from '../gestaltung/bausteine';
import { Auswahlsuche } from '../gestaltung/Auswahlsuche';
import { Dateiablage, type GewaehlteDatei } from '../gestaltung/Dateiablage';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler, anlegen } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { supabase } from '../supabase';
import { dokumentHochladen } from '../daten/auftraege';
import {
  fahrzeugeLaden,
  kundeAnlegen,
  kundenLaden,
  ortAnlegen,
  orteLaden,
  vorlagenLaden,
} from '../daten/stammdaten';
import { mitarbeiterLaden } from '../daten/mitarbeiter';
import { fahrerZurWahl } from '../daten/fahrerwahl';
import { heute } from '../daten/kalender';
import { anschriftszeile } from '../daten/anschrift';
import { zahlAlsText, zahlLesen, zahlOderNull } from '../daten/zahl';
import {
  entwurfLesen,
  entwurfLoeschen,
  entwurfSchreiben,
  fluechtigLesen,
  fluechtigLoeschen,
  fluechtigSchreiben,
} from '../daten/entwurf';
import { EINHEITTEXT } from './StreckenSeite';

const ARTEN = [
  { wert: 'standard', text: 'Standard' },
  { wert: 'pendel', text: 'Pendelverkehr' },
  { wert: 'sonstige', text: 'Sonstige' },
  { wert: 'fremdvergabe', text: 'Fremdvergabe' },
];

const DOKUMENTARTEN = [
  { wert: 'auftragsdokument', text: 'Transportauftrag des Kunden' },
  { wert: 'sonstiges', text: 'Sonstiges' },
  { wert: 'nachweis', text: 'Nachweis' },
];

/** Was vom Formular einen Seitenwechsel überlebt. Dateien können es nicht. */
type Entwurf = {
  art: string;
  kundeId: string | null;
  ladeortId: string | null;
  entladeortId: string | null;
  material: string;
  ladedatum: string;
  externeNummer: string;
  notiz: string;
  fahrerId: string;
  fahrzeugId: string;
  preisart: string;
  preis: string;
  mengeneinheit: string;
  /** Erst später dazugekommen — ältere Entwürfe im Speicher haben es nicht. */
  relationen?: { ladeortId: string | null; entladeortId: string | null }[];
  /**
   * Nur die NAMEN der gewählten Dateien (HJ-025). Die Dateien selbst liegen im
   * Arbeitsspeicher; fehlen sie dort (Neuladen), sagt der Hinweis, welche.
   */
  dateinamen?: string[];
};

/** Eine Strecke einer Pendeltour. Die Kennung dient nur als React-Schlüssel. */
type Relation = {
  schluessel: string;
  ladeortId: string | null;
  entladeortId: string | null;
};

function leereRelation(): Relation {
  return { schluessel: crypto.randomUUID(), ladeortId: null, entladeortId: null };
}

/** Eine Meldung, die nach dem Anlegen noch dazukommt: Die Tour steht, etwas fehlt. */
type Nachmeldung = { titel: string; hinweis: string; zeilen: string[] };

/** Was ein Fehler sagt — der Text des Servers, nicht bloß „ging nicht". */
function grundVon(problem: unknown): string {
  if (problem instanceof Speicherfehler) {
    return problem.klar.original || problem.klar.text;
  }
  return problem instanceof Error ? problem.message : 'Unbekannter Fehler';
}

const ENTWURF = 'neue-tour';

export function AuftragAnlegen(eigenschaften: {
  /** Das Formular zumachen — durch „Abbrechen" oder „Schließen". */
  schliessen: () => void;
  /** Eine Tour steht. Die Liste dahinter frischt auf, das Formular bleibt. */
  eingetragen: () => void;
  /** Zur Tour springen — nur, wenn die Person es ausdrücklich will. */
  oeffneTour: (auftragId: string) => void;
}) {
  const kunden = useAbfrage(kundenLaden, []);
  const orte = useAbfrage(orteLaden, []);
  const fahrzeuge = useAbfrage(fahrzeugeLaden, []);
  const leute = useAbfrage(mitarbeiterLaden, []);
  const vorlagen = useAbfrage(vorlagenLaden, []);

  // Örtlicher Kalendertag: `toISOString` rechnet nach UTC und liefert abends
  // nach 22 Uhr den Vortag — die Tour hinge dann sofort als „offen“ am
  // gestrigen Datum statt als „geplant“ am heutigen.
  const heutigerTag = heute();

  // EINMAL beim Aufbauen gelesen, nicht bei jedem Rendern: Der Entwurf ist der
  // Startwert der Felder, nicht ihre Quelle. Läse man ihn laufend, überschriebe
  // er beim nächsten Rendern, was gerade getippt wurde.
  const [alt] = useState(() => entwurfLesen<Entwurf>(ENTWURF));

  const [art, setArt] = useState(alt?.art ?? 'standard');
  const [kundeId, setKundeId] = useState<string | null>(alt?.kundeId ?? null);
  const [ladeortId, setLadeortId] = useState<string | null>(alt?.ladeortId ?? null);
  const [entladeortId, setEntladeortId] = useState<string | null>(
    alt?.entladeortId ?? null,
  );
  const [material, setMaterial] = useState(alt?.material ?? '');
  const [ladedatum, setLadedatum] = useState(alt?.ladedatum ?? heutigerTag);
  const [externeNummer, setExterneNummer] = useState(alt?.externeNummer ?? '');
  const [notiz, setNotiz] = useState(alt?.notiz ?? '');
  const [fahrerId, setFahrerId] = useState(alt?.fahrerId ?? '');
  const [fahrzeugId, setFahrzeugId] = useState(alt?.fahrzeugId ?? '');
  const [preisart, setPreisart] = useState(alt?.preisart ?? '');
  const [preis, setPreis] = useState(alt?.preis ?? '');
  const [mengeneinheit, setMengeneinheit] = useState(alt?.mengeneinheit ?? 't');
  const [dateien, setDateien] = useState<GewaehlteDatei[]>(
    () => fluechtigLesen<GewaehlteDatei[]>(ENTWURF) ?? [],
  );

  /**
   * Zählt mit jeder eingetragenen Tour hoch und steckt im `key` der Felder,
   * die eigenen Zustand haben (Suchtext, Trefferliste, Dateiliste) — siehe
   * „Nach dem Eintragen ist das Formular wirklich leer" oben.
   */
  const [runde, setRunde] = useState(0);
  const kundenfeld = useRef<HTMLDivElement>(null);

  // Nach jeder eingetragenen Tour zurück an den Anfang. Ein Effekt, weil das
  // Feld erst NACH dem Neuaufbau existiert, den `runde` auslöst.
  useEffect(() => {
    if (runde === 0) return;
    kundenfeld.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [runde]);

  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [nachmeldungen, setNachmeldungen] = useState<Nachmeldung[]>([]);
  const [relationen, setRelationen] = useState<Relation[]>(() =>
    alt?.relationen && alt.relationen.length > 0
      ? alt.relationen.map((r) => ({ ...r, schluessel: crypto.randomUUID() }))
      : [leereRelation()],
  );
  const [vorlagenOffen, setVorlagenOffen] = useState(false);

  /**
   * ═══ DIE SPERRE GEGEN DIE ZWEITE TOUR ═══
   *
   * Gesetzt, sobald die Tour in der Datenbank steht. Ab da legt dieses Formular
   * nichts mehr an, egal was danach passiert.
   *
   * Im Normalfall sieht man diesen Zustand nie — das Formular schließt sofort.
   * Sichtbar wird er nur, wenn nach dem Anlegen noch etwas schiefgeht, und das
   * ist genau der Fall, der aus dem Büro gemeldet wurde: Der Datei-Upload
   * scheiterte, das Formular blieb mit allen Eingaben stehen, der Knopf war
   * wieder anklickbar — und der zweite Klick legte dieselbe Tour ein zweites
   * Mal an.
   *
   * Die Kennung statt eines bloßen `true`, weil sie zugleich der Weg hinaus
   * ist: Der Knopf führt danach zu der Tour, die gerade entstanden ist.
   */
  const [angelegt, setAngelegt] = useState<string | null>(null);

  /**
   * Die zuletzt eingetragene Tour. Sie steht als Bestätigung über dem leeren
   * Formular — sonst ist nach dem Speichern nicht zu sehen, ob es geklappt hat:
   * Die Felder sind leer, und leer waren sie vorher auch.
   */
  const [zuletzt, setZuletzt] = useState<{ id: string; nummer: string | null } | null>(
    null,
  );

  /** Steht die Tour, wird an ihren Feldern nichts mehr getippt. */
  const gesperrt = laeuft || angelegt !== null;

  // Nach jeder Änderung sichern. Das ist billig — ein paar Dutzend Zeichen —
  // und die einzige Stelle, an der gesichert wird: Ein zweiter Aufruf an einem
  // einzelnen Feld wäre eine zweite Stelle, die man beim nächsten neuen Feld
  // vergessen kann.
  useEffect(() => {
    entwurfSchreiben<Entwurf>(ENTWURF, {
      art,
      kundeId,
      ladeortId,
      entladeortId,
      material,
      ladedatum,
      externeNummer,
      notiz,
      fahrerId,
      fahrzeugId,
      preisart,
      preis,
      mengeneinheit,
      relationen: relationen.map((r) => ({
        ladeortId: r.ladeortId,
        entladeortId: r.entladeortId,
      })),
      dateinamen: dateien.map((d) => d.datei.name),
    });
    fluechtigSchreiben(ENTWURF, dateien);
  }, [
    dateien,
    relationen,
    art,
    kundeId,
    ladeortId,
    entladeortId,
    material,
    ladedatum,
    externeNummer,
    notiz,
    fahrerId,
    fahrzeugId,
    preisart,
    preis,
    mengeneinheit,
  ]);

  /**
   * Wurde ein Entwurf wiederhergestellt, der wirklich etwas enthält?
   *
   * Ein Entwurf, in dem nur das voreingestellte Ladedatum steht, ist keiner —
   * dafür einen Hinweis zu zeigen, wäre die Sorte Meldung, die man nach dem
   * dritten Mal nicht mehr liest.
   */
  const [entwurfGefunden] = useState(
    () =>
      alt !== null &&
      (alt.kundeId !== null ||
        alt.ladeortId !== null ||
        alt.entladeortId !== null ||
        alt.material.trim() !== '' ||
        alt.notiz.trim() !== '' ||
        alt.externeNummer.trim() !== '' ||
        alt.preis.trim() !== '' ||
        (alt.dateinamen ?? []).length > 0 ||
        (alt.relationen ?? []).some((r) => r.ladeortId || r.entladeortId)),
  );
  const [hinweisWeg, setHinweisWeg] = useState(false);

  /**
   * Welche Dateien im Entwurf standen, aber nicht mehr da sind — nach einem
   * Neuladen. Genau diese Namen stehen im Hinweis; ein allgemeines „Dateien
   * musst du neu wählen" hieße, dass man raten muss, welche es waren.
   */
  const [verloreneDateien] = useState(() => {
    const noch = new Set(dateien.map((d) => d.datei.name));
    return (alt?.dateinamen ?? []).filter((name) => !noch.has(name));
  });

  /**
   * Steht die Tour, aber etwas danach fehlt (Fahrt, Strecke, Datei), ist
   * dieses Formular fertig — sein einziger Weg ist der zur Tour. Dann geht es
   * dabei zu: Sonst stünde es seit HJ-025 beim nächsten Besuch der
   * Disposition leer wieder offen da, ohne dass jemand es gewollt hat. Nach
   * einem ERFOLG bleibt es dagegen offen — dort ist „noch eine Tour" der
   * Normalfall.
   */
  function zurAngelegtenTour(id: string) {
    // Erst schließen (nimmt `?neu=1` aus der Adresse), dann springen: Der
    // Sprung kommt zuletzt und gewinnt. Andersherum schrieb das Schließen
    // die Adresse zurück auf `/disposition`, und man landete in der Liste.
    eigenschaften.schliessen();
    eigenschaften.oeffneTour(id);
  }

  function verwerfen() {
    entwurfLoeschen(ENTWURF);
    fluechtigLoeschen(ENTWURF);
    eigenschaften.schliessen();
  }

  /**
   * Beide Ortsfelder legen auf demselben Weg an — deshalb steht er einmal hier
   * und nicht zweimal in der Anzeige. Nach dem Anlegen wird die Liste
   * aufgefrischt: Sonst kennt das ANDERE Ortsfeld den neuen Ort nicht, und wer
   * eine Pendeltour von und nach demselben neuen Ort einträgt, legt ihn zweimal
   * an.
   */
  async function ortHierAnlegen(name: string, zusatz: Record<string, string>) {
    const neu = await ortAnlegen({
      name,
      strasse: zusatz.strasse,
      plz: zusatz.plz,
      ortsname: zusatz.ortsname,
    });
    orte.auffrischen();
    return {
      id: neu.id,
      name: neu.name,
      unter: anschriftszeile(neu.strasse, neu.plz, neu.ortsname),
    };
  }

  /** „Hauptstraße 12, 72458 Albstadt" — woran zwei Standorte auseinandergehen. */
  const ortsauswahl = (orte.daten ?? [])
    .filter((o) => o.aktiv)
    .map((o) => ({
      id: o.id,
      name: o.name,
      unter: anschriftszeile(o.strasse, o.plz, o.ortsname),
    }));

  // Die Straße gehört seit HJ-024 dazu: Im Testlauf musste sie nach jeder
  // Tour unter „Orte“ nachgetragen werden — ein zweiter Gang für jeden neuen
  // Ort. Wie PLZ und Ort KEINE Pflicht: Feldwege und Baustellen haben oft
  // keine Anschrift, und ein Pflichtfeld würde dort mit „-“ gefüllt.
  const ORTSFELDER = [
    { schluessel: 'strasse', marke: 'Straße', platzhalter: 'z. B. Hauptstraße 12' },
    { schluessel: 'plz', marke: 'PLZ', platzhalter: 'z. B. 72458' },
    { schluessel: 'ortsname', marke: 'Ort', platzhalter: 'z. B. Albstadt' },
  ];

  // Beim Kunden dieselben drei: Mehrere Kunden heißen gleich (HJ-042), und ein
  // zweiter „Korn Recycling" ohne Anschrift sähe in der Auswahl aus wie der erste.
  const KUNDENFELDER = ORTSFELDER.map((f) =>
    f.schluessel === 'ortsname' ? { ...f, schluessel: 'ort' } : f,
  );

  /**
   * ═══ DER PREISVORSCHLAG AUS DER STRECKENVORLAGE ═══
   *
   * Sobald Von und Nach zu einer gepflegten Vorlage passen, steht ihr Preis
   * zur Übernahme bereit. Bewusst NICHT automatisch eingetragen: Ein Preis,
   * der von selbst im Feld erscheint, wird nicht gelesen — und wenn er im
   * Einzelfall nicht gilt, steht er trotzdem auf der Rechnung. Ein Knopf
   * kostet eine Sekunde und erzwingt einen Blick.
   */
  const pendel = art === 'pendel';

  /**
   * Die Relationen, die wirklich etwas enthalten. Eine ganz leere Zeile ist
   * nur ein Platzhalter und zählt nicht; eine halb ausgefüllte zählt als
   * Fehler (`relationHalb`) — sie stillschweigend wegzulassen hieße, dass eine
   * Strecke fehlt, von der der Fahrer nichts erfährt.
   */
  const vollRelationen = relationen.filter(
    (r) => r.ladeortId !== null && r.entladeortId !== null,
  );
  const relationHalb = relationen.some(
    (r) => (r.ladeortId === null) !== (r.entladeortId === null),
  );

  // Am Auftrag stehen bei Pendel die Orte der ERSTEN Relation — genau wie in
  // der App. Sonst zeigte die Disposition „— → —", obwohl die Tour Strecken hat.
  const kopfLadeortId = pendel ? (vollRelationen[0]?.ladeortId ?? null) : ladeortId;
  const kopfEntladeortId = pendel
    ? (vollRelationen[0]?.entladeortId ?? null)
    : entladeortId;

  const vorschlag = useMemo(() => {
    if (!kopfLadeortId || !kopfEntladeortId) return null;
    return (
      (vorlagen.daten ?? []).find(
        (v) =>
          v.ladeort_id === kopfLadeortId &&
          v.entladeort_id === kopfEntladeortId &&
          v.preis_vk !== null,
      ) ?? null
    );
  }, [vorlagen.daten, kopfLadeortId, kopfEntladeortId]);

  const eingegebenerPreis = zahlLesen(preis);
  const vorschlagUebernommen =
    vorschlag !== null &&
    preisart === vorschlag.preisart &&
    eingegebenerPreis.art === 'zahl' &&
    eingegebenerPreis.wert === vorschlag.preis_vk;

  const orteNoetig = art === 'standard';
  const vollstaendig =
    kundeId !== null &&
    ladedatum !== '' &&
    eingegebenerPreis.art !== 'ungueltig' &&
    (pendel
      ? vollRelationen.length > 0 && !relationHalb
      : !orteNoetig || (ladeortId !== null && entladeortId !== null));

  function relationAendern(schluessel: string, werte: Partial<Relation>) {
    setRelationen((bisher) =>
      bisher.map((r) => (r.schluessel === schluessel ? { ...r, ...werte } : r)),
    );
  }

  function relationEntfernen(schluessel: string) {
    setRelationen((bisher) => {
      const rest = bisher.filter((r) => r.schluessel !== schluessel);
      // Eine Pendeltour ohne Zeile wäre ein Formular ohne Eingabe.
      return rest.length > 0 ? rest : [leereRelation()];
    });
  }

  /** Eine Vorlage wird zur Zeile. Steht nur die leere Startzeile da, ersetzt sie diese. */
  function vorlageUebernehmen(ladeort: string, entladeort: string) {
    setRelationen((bisher) => {
      const neu = { schluessel: crypto.randomUUID(), ladeortId: ladeort, entladeortId: entladeort };
      const nurLeer = bisher.every((r) => r.ladeortId === null && r.entladeortId === null);
      return nurLeer ? [neu] : [...bisher, neu];
    });
    setVorlagenOffen(false);
  }

  const ortsname = (id: string | null) =>
    (orte.daten ?? []).find((o) => o.id === id)?.name ?? '?';

  async function speichern() {
    // `angelegt` ist der eigentliche Schutz. Ohne ihn genügte ein gescheiterter
    // Datei-Upload, um eine zweite gleiche Tour möglich zu machen.
    if (!vollstaendig || laeuft || angelegt) return;
    setLaeuft(true);
    setFehler(null);
    setNachmeldungen([]);

    // ═══ EIN `finally` FÜR `laeuft` ═══
    //
    // Vorher stand `setLaeuft(false)` an den beiden Fehlerzweigen — und im
    // Erfolgsfall nirgends. Der Knopf blieb auf „Wird eingetragen …", alle
    // Felder gesperrt, obwohl die Tour längst stand und die Erfolgsmeldung
    // versprach, man könne direkt die nächste eintragen. Ein Zustand, der an
    // drei Stellen von Hand zurückgesetzt werden muss, wird an der vierten
    // vergessen; an EINER Stelle kann er nicht fehlen.
    try {
      await eintragen();
    } finally {
      setLaeuft(false);
    }
  }

  async function eintragen() {
    let auftrag: { id: string; nummer: string | null };
    try {
      auftrag = await anlegen<{ id: string; nummer: string | null }>(
        'transportauftrag',
        {
          art,
          kunde_id: kundeId,
          ladeort_id: kopfLadeortId,
          entladeort_id: kopfEntladeortId,
          material: material.trim() || null,
          ladedatum,
          externe_nummer: externeNummer.trim() || null,
          notiz: notiz.trim() || null,
          preisart: preisart || null,
          // Die Vollständigkeitsprüfung schließt Unsinn aus; `?? null` nur für
          // den Typ.
          preis_vk: zahlOderNull(preis) ?? null,
          mengeneinheit: preisart === 'menge' ? mengeneinheit : null,
        },
      );
    } catch (problem) {
      // Nur HIER steht die Tour noch nicht. Das Formular bleibt, wie es war.
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
      return;
    }

    // ═══ AB HIER STEHT DIE TOUR — ALSO WEG MIT DEM ENTWURF ═══
    //
    // Genau hier und nicht am Ende: Scheitert gleich die Fahrt oder ein
    // Datei-Upload, bleibt dieser Bildschirm mit einer Meldung stehen. Läge
    // der Entwurf dann noch da, käme er beim nächsten „Neue Tour" wieder hoch —
    // und die Tour, die bereits existiert, würde ein zweites Mal eingetragen.
    //
    // Umgekehrt darf er vorher NICHT weg: Scheitert das Anlegen selbst, steht
    // das Formular noch, und ein Netzfehler soll nicht die Eingabe kosten.
    entwurfLoeschen(ENTWURF);
    fluechtigLoeschen(ENTWURF);

    // Und aus demselben Grund direkt daneben: Ab hier legt dieses Formular
    // nichts mehr an. Beides gehört an dieselbe Stelle — den Punkt, an dem die
    // Tour existiert. Gilt für JEDEN Fehler danach, nicht nur für Dateien.
    setAngelegt(auftrag.id);

    const meldungen: Nachmeldung[] = [];

    // Die Kennung kommt vom Browser — dieselbe Regel wie in der App: Wer sie
    // selbst vergibt, darf eine Zustellung wiederholen, ohne einen zweiten
    // Datensatz zu erzeugen.
    const fahrtId = crypto.randomUUID();
    let fahrtSteht = false;
    try {
      const { error } = await supabase.from('fahrt').insert({
        id: fahrtId,
        auftrag_id: auftrag.id,
        rolle: 'komplett',
        fahrer_id: fahrerId || null,
        fahrzeug_id: fahrzeugId || null,
        datum: ladedatum,
      });
      if (error) throw new Speicherfehler(alsKlarfehler(error));
      fahrtSteht = true;
    } catch (problem) {
      meldungen.push({
        titel: 'Die Tour ist eingetragen — die Fahrt fehlt noch',
        hinweis:
          'Ohne Fahrt taucht die Tour bei keinem Fahrer auf. Trag sie in der Detailsicht der Tour nach. Was der Server dazu gesagt hat:',
        zeilen: [grundVon(problem)],
      });
    }

    // Je Relation ein Fuhrenblock mit Anzahl 0: Anlegen ist planen, gezählt
    // wird beim Fahren. Eine Fuhrenzahl, die morgens geraten wird, bleibt
    // falsch stehen, weil sie abends niemand mehr prüft.
    //
    // Ohne Fahrt gibt es nichts, woran sie hängen könnten — dann steht die
    // Fahrt in der Meldung, und die Strecken lassen sich nachtragen, sobald es
    // sie gibt.
    if (pendel && fahrtSteht) {
      const strecken: string[] = [];
      // Die Kennungen entstehen VOR dem ersten Versuch, nicht im Versuch.
      const kennungen = vollRelationen.map(() => crypto.randomUUID());
      for (let i = 0; i < vollRelationen.length; i++) {
        const r = vollRelationen[i];
        try {
          const { error } = await supabase.from('fuhrenblock').insert({
            id: kennungen[i],
            fahrt_id: fahrtId,
            ladeort_id: r.ladeortId,
            entladeort_id: r.entladeortId,
            anzahl: 0,
            mengeneinheit: 'fuhre',
          });
          if (error) throw new Speicherfehler(alsKlarfehler(error));
        } catch (problem) {
          strecken.push(
            `${ortsname(r.ladeortId)} → ${ortsname(r.entladeortId)}: ${grundVon(problem)}`,
          );
        }
      }
      if (strecken.length > 0) {
        meldungen.push({
          titel: 'Die Tour ist eingetragen — eine Strecke fehlt noch',
          hinweis:
            'Ohne Strecke kann der Fahrer keine Fuhre zählen. Trag sie in der Detailsicht der Tour nach. Was der Server dazu gesagt hat:',
          zeilen: strecken,
        });
      }
    }

    // Ein gescheiterter Upload nimmt die Tour nicht mehr zurück — er wird
    // gemeldet, und die Datei lässt sich in der Detailsicht nachreichen.
    //
    // ═══ WARUM HIER SO VIEL IN DIE MELDUNG WANDERT ═══
    //
    // Vorher stand nur der Dateiname da, und die eigentliche Antwort des
    // Servers ging verloren. Ein abgelehntes PDF von 71 KB sah deshalb aus wie
    // ein Größenproblem — und die Suche danach ging in die falsche Richtung.
    // Was hier steht, muss die Frage „warum ging das nicht" allein beantworten:
    // welche Datei, welchen Typ der Browser gemeldet hat, wie groß sie war und
    // was der Server wörtlich sagte.
    const gescheitert: string[] = [];
    for (const { datei, art: dokumentart } of dateien) {
      try {
        await dokumentHochladen(auftrag.id, datei, dokumentart);
      } catch (problem) {
        const kb = Math.max(1, Math.round(datei.size / 1024));
        gescheitert.push(
          `${datei.name} (${datei.type || 'Typ unbekannt'}, ${kb} KB): ${grundVon(problem)}`,
        );
      }
    }
    if (gescheitert.length > 0) {
      meldungen.push({
        titel: 'Die Tour ist eingetragen — eine Datei fehlt noch',
        hinweis:
          'Trag die Datei in der Detailsicht der Tour nach. Was der Server dazu gesagt hat:',
        zeilen: gescheitert,
      });
    }

    // Die Tour steht — die Liste dahinter frischt in JEDEM Fall auf, auch wenn
    // gleich eine Meldung stehen bleibt. Sonst fehlt genau die Tour, um die es
    // in der Meldung geht.
    eigenschaften.eingetragen();

    if (meldungen.length > 0) {
      setNachmeldungen(meldungen);
      return;
    }

    // ═══ NACH DEM EINTRAGEN BLEIBT DAS FORMULAR STEHEN ═══
    //
    // Vorher schloss es sich und sprang in die Detailsicht der neuen Tour. Aus
    // dem Büro kam dazu: Wer den Transportauftrag als PDF danebenliegen hat und
    // fünf Touren nacheinander einträgt, muss jedes Mal zurück und „Tour
    // eintragen" neu drücken — und der Absprung in die Detailsicht ist genau
    // die Bewegung, die man dabei nicht braucht.
    //
    // Also: leeren und stehenbleiben. Wer doch hinein will, hat den Weg in der
    // Erfolgsmeldung; wer fertig ist, drückt „Schließen".
    zuruecksetzen();
    setZuletzt({ id: auftrag.id, nummer: auftrag.nummer ?? null });
  }

  /** Alle Felder auf Anfang — außer dem Ladedatum (E-052). */
  function zuruecksetzen() {
    setArt('standard');
    setFahrerId('');
    setFahrzeugId('');
    setPreisart('');
    setMengeneinheit('t');
    setKundeId(null);
    setLadeortId(null);
    setEntladeortId(null);
    setRelationen([leereRelation()]);
    setVorlagenOffen(false);
    setMaterial('');
    setExterneNummer('');
    setNotiz('');
    setPreis('');
    setDateien([]);
    setAngelegt(null);
    setNachmeldungen([]);
    setFehler(null);
    setRunde((n) => n + 1);
    // Der Hinweis „Dein angefangener Eintrag ist wieder da" gehörte zur
    // EBEN eingetragenen Tour. Stünde er weiter über dem leeren Formular —
    // samt der Dateinamen, die neu zu wählen seien —, hängte jemand die PDF
    // der vorigen Tour an die nächste (Review HJ-025).
    setHinweisWeg(true);
  }

  const fahrer = fahrerZurWahl(leute.daten ?? []);
  const wagen = (fahrzeuge.daten ?? []).filter((f) => f.aktiv);

  return (
    <Karte
      titel="Neue Tour"
      // Im Kopf der Karte, nicht im Kopf der Seite (HJ-035): Dort wirkte der
      // Knopf nicht zum Formular gehörig. Abbrechen wirft den Entwurf weg —
      // bliebe er liegen, käme er beim nächsten „Neue Tour" wieder hoch, und
      // dann trägt jemand die Tour ein, die er gerade verworfen hat. Gesperrt,
      // solange eingetragen wird: Ein Abbruch mittendrin bräche nichts ab, die
      // Tour entstünde trotzdem, nur ohne dass es jemand sieht.
      werkzeuge={
        <Knopf art="still" klein disabled={laeuft} onClick={verwerfen}>
          {zuletzt || angelegt ? 'Schließen' : 'Abbrechen'}
        </Knopf>
      }
    >
      <div className="stapel">
        {/* ═══ DIE BESTÄTIGUNG ÜBER DEM LEEREN FORMULAR ═══

            Ohne sie wäre nach dem Speichern nicht zu sehen, ob es geklappt hat:
            Die Felder sind leer — und leer waren sie vorher auch. Die
            Auftragsnummer steht dabei, weil sie das ist, was man gleich in
            SevDesk oder auf dem Papier wiederfindet. */}
        {zuletzt && !angelegt && (
          <Meldung
            art="gut"
            titel={`Tour ${zuletzt.nummer ?? ''} ist eingetragen`.replace('  ', ' ')}
            knopf={{
              text: 'Zur Tour',
              tun: () => eigenschaften.oeffneTour(zuletzt.id),
            }}
          >
            Du kannst direkt die nächste eintragen — das Formular bleibt offen.
          </Meldung>
        )}

        {fehler && <Fehlerkasten fehler={fehler} />}

        {/* Steht die Tour, kam aber etwas danach nicht durch (Fahrt, Strecke,
            Datei), ist dieser Bildschirm fertig: Er darf nur noch WEITERFÜHREN,
            nicht noch einmal anlegen. Deshalb tragen die Meldungen den Weg
            hinaus, und der Knopf unten heißt nicht mehr „Tour eintragen". */}
        {nachmeldungen.map((m) => (
          <Meldung
            key={m.titel}
            art="warnung"
            titel={m.titel}
            knopf={
              angelegt
                ? { text: 'Zur Tour', tun: () => zurAngelegtenTour(angelegt) }
                : undefined
            }
          >
            <p style={{ margin: '0 0 6px' }}>{m.hinweis}</p>
            <ul className="fehltliste">
              {m.zeilen.map((zeile) => (
                <li key={zeile}>{zeile}</li>
              ))}
            </ul>
          </Meldung>
        ))}

        {/* Der Hinweis sagt zwei Dinge, und beide sind nötig: dass die Eingaben
            wieder da sind (sonst tippt jemand sicherheitshalber nochmal) und
            dass die Dateien es NICHT sind (sonst fehlt hinterher der
            Transportauftrag des Kunden, und niemand weiß warum). */}
        {entwurfGefunden && !hinweisWeg && (
          <Meldung
            art="neutral"
            titel="Dein angefangener Eintrag ist wieder da"
            knopf={{ text: 'Verwerfen und neu anfangen', tun: verwerfen }}
          >
            {verloreneDateien.length > 0
              ? `Diese Dateien musst du noch einmal auswählen — nach dem Neuladen kann der Browser sie nicht aufheben: ${verloreneDateien.join(', ')}.`
              : dateien.length > 0
                ? 'Die ausgewählten Dateien sind auch wieder da.'
                : 'Dateien, die du ab jetzt auswählst, bleiben beim Wechsel innerhalb des Backoffice erhalten — beim Neuladen der Seite nicht.'}{' '}
            <button
              type="button"
              className="knopf knopf--still knopf--klein"
              onClick={() => setHinweisWeg(true)}
            >
              Verstanden
            </button>
          </Meldung>
        )}

        <div className="feldreihe">
          <label className="feld">
            <span className="feld__marke">Art</span>
            <select value={art} disabled={gesperrt} onChange={(e) => setArt(e.target.value)}>
              {ARTEN.map((a) => (
                <option key={a.wert} value={a.wert}>
                  {a.text}
                </option>
              ))}
            </select>
          </label>

          {/* `display: contents`: Der Rahmen ist nur der Griff für den Fokus
              nach dem Eintragen und darf das Raster der Feldreihe nicht stören. */}
          <div ref={kundenfeld} style={{ display: 'contents' }}>
            <Auswahlsuche
              key={`kunde-${runde}`}
              marke="Kunde"
              wert={kundeId}
              setzen={setKundeId}
              auswahl={(kunden.daten ?? [])
                .filter((k) => k.aktiv)
                .map((k) => ({
                  id: k.id,
                  name: k.name,
                  unter: anschriftszeile(k.strasse, k.plz, k.ort),
                }))}
              disabled={gesperrt}
              neuAnlegen={{
                was: 'Kunden',
                zusatzfelder: KUNDENFELDER,
                anlegen: async (name, zusatz) => {
                  const neu = await kundeAnlegen({
                    name,
                    strasse: zusatz.strasse,
                    plz: zusatz.plz,
                    ort: zusatz.ort,
                  });
                  kunden.auffrischen();
                  return {
                    id: neu.id,
                    name: neu.name,
                    unter: anschriftszeile(neu.strasse, neu.plz, neu.ort),
                  };
                },
              }}
            />
          </div>

          <label className="feld">
            <span className="feld__marke">Ladedatum</span>
            <input
              type="date"
              value={ladedatum}
              disabled={gesperrt}
              onChange={(e) => setLadedatum(e.target.value)}
            />
          </label>
        </div>

        {/* ═══ ORTE LASSEN SICH HIER ANLEGEN ═══

            Gefragt wird nach Straße, PLZ und Ort (HJ-024) — alle drei
            freiwillig: Wer die Adresse nicht zur Hand hat, legt mit dem NAMEN
            an und trägt den Rest später unter „Orte" nach. Der Datensatz gilt trotzdem als
            geprüft (`bestaetigt: true` in `ortAnlegen`) — das Merkmal
            unterscheidet „vom Büro angelegt" von „vom Fahrer unterwegs
            eingetippt", nicht „vollständig" von „unvollständig".

            Der Umweg über die Stammdaten und zurück hat vorher das halbe
            Formular gekostet. Beides ist jetzt behoben, aber unabhängig
            voneinander: Auch mit Entwurf bleibt der Umweg ein Umweg. */}
        {pendel ? (
          /* ═══ RELATIONEN STATT VON UND NACH ═══

             Jede Zeile ist eine Strecke, für die der Fahrer Fuhren zählt.
             „Aus Streckenvorlage" übernimmt eine gepflegte Strecke als Zeile —
             wer dieselbe Pendelstrecke jede Woche fährt, tippt sie nicht jedes
             Mal neu und erzeugt keine neue Schreibweise. Mindestens eine
             vollständige Strecke ist Pflicht: Ohne sie entsteht wieder eine
             Tour, in der der Fahrer nichts zählen kann. */
          <div className="stapel" data-relationen>
            <div className="reihe" style={{ justifyContent: 'space-between' }}>
              <span className="feld__marke">Relationen</span>
              <Knopf
                art="zweit"
                klein
                disabled={gesperrt || (vorlagen.daten ?? []).every((v) => !v.aktiv)}
                onClick={() => setVorlagenOffen((o) => !o)}
              >
                Aus Streckenvorlage
              </Knopf>
            </div>

            {vorlagenOffen && (
              <div className="stapel" style={{ gap: 6 }}>
                {(vorlagen.daten ?? [])
                  .filter((v) => v.aktiv)
                  .map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className="wahlzeile"
                      onClick={() => vorlageUebernehmen(v.ladeort_id, v.entladeort_id)}
                    >
                      <span>
                        {v.bezeichnung ? `${v.bezeichnung} · ` : ''}
                        {v.ladeort} → {v.entladeort}
                      </span>
                    </button>
                  ))}
              </div>
            )}

            {relationen.map((r, nr) => (
              <div className="feldreihe" key={r.schluessel} data-relation>
                <Auswahlsuche
                  marke={`Strecke ${nr + 1}: Von`}
                  wert={r.ladeortId}
                  setzen={(id) => relationAendern(r.schluessel, { ladeortId: id })}
                  auswahl={ortsauswahl}
                  disabled={gesperrt}
                  neuAnlegen={{ was: 'Ort', zusatzfelder: ORTSFELDER, anlegen: ortHierAnlegen }}
                />
                <Auswahlsuche
                  marke={`Strecke ${nr + 1}: Nach`}
                  wert={r.entladeortId}
                  setzen={(id) => relationAendern(r.schluessel, { entladeortId: id })}
                  auswahl={ortsauswahl}
                  disabled={gesperrt}
                  neuAnlegen={{ was: 'Ort', zusatzfelder: ORTSFELDER, anlegen: ortHierAnlegen }}
                />
                <div className="reihe" style={{ alignItems: 'flex-end' }}>
                  <Knopf
                    art="still"
                    klein
                    disabled={gesperrt || (relationen.length === 1 && !r.ladeortId && !r.entladeortId)}
                    onClick={() => relationEntfernen(r.schluessel)}
                  >
                    Entfernen
                  </Knopf>
                </div>
              </div>
            ))}

            <div className="reihe">
              <Knopf
                art="zweit"
                klein
                disabled={gesperrt}
                onClick={() => setRelationen((bisher) => [...bisher, leereRelation()])}
              >
                Weitere Relation
              </Knopf>
              <span className={relationHalb || vollRelationen.length === 0 ? 'meta feld__fehler' : 'meta'}>
                {relationHalb
                  ? 'Eine Strecke ist nur halb ausgefüllt — Von und Nach gehören zusammen.'
                  : vollRelationen.length === 0
                    ? 'Mindestens eine vollständige Strecke ist nötig, sonst kann der Fahrer keine Fuhre zählen.'
                    : 'Je Strecke legt das System einen Fuhrenblock mit 0 Fuhren an — gezählt wird beim Fahren.'}
              </span>
            </div>
          </div>
        ) : (
          <div className="feldreihe">
            <Auswahlsuche
              key={`von-${runde}`}
              marke={orteNoetig ? 'Von' : 'Von (optional)'}
              wert={ladeortId}
              setzen={setLadeortId}
              auswahl={ortsauswahl}
              disabled={gesperrt}
              neuAnlegen={{
                was: 'Ort',
                zusatzfelder: ORTSFELDER,
                anlegen: ortHierAnlegen,
              }}
            />
            <Auswahlsuche
              key={`nach-${runde}`}
              marke={orteNoetig ? 'Nach' : 'Nach (optional)'}
              wert={entladeortId}
              setzen={setEntladeortId}
              auswahl={ortsauswahl}
              disabled={gesperrt}
              neuAnlegen={{
                was: 'Ort',
                zusatzfelder: ORTSFELDER,
                anlegen: ortHierAnlegen,
              }}
            />
          </div>
        )}

        <div className="feldreihe">
          <Textfeld
            marke="Material"
            wert={material}
            setzen={setMaterial}
            disabled={gesperrt}
          />
          <Textfeld
            marke="Auftragsnummer des Kunden"
            wert={externeNummer}
            setzen={setExterneNummer}
            disabled={gesperrt}
          />
          <label className="feld">
            <span className="feld__marke">Fahrer</span>
            <select
              value={fahrerId}
              disabled={gesperrt}
              onChange={(e) => setFahrerId(e.target.value)}
            >
              <option value="">Noch offen</option>
              {fahrer.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.text}
                </option>
              ))}
            </select>
          </label>
          <label className="feld">
            <span className="feld__marke">Fahrzeug</span>
            <select
              value={fahrzeugId}
              disabled={gesperrt}
              onChange={(e) => setFahrzeugId(e.target.value)}
            >
              <option value="">Noch offen</option>
              {wagen.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.kurzname ?? w.kennzeichen}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* ═══ WARUM DER PREIS SCHON HIER STEHT ═══

            Weil die Auswertung sonst nichts über die kommende Woche sagen kann.
            Wird der Preis erst beim Abrechnen eingetragen, kennt das System
            immer nur die Vergangenheit — und die Frage „was ist gerade an
            Umsatz auf der Straße" bleibt unbeantwortet.

            Bleibt das Feld leer, ist das kein Fehler. Nicht jeder Preis steht
            beim Eintragen schon fest; er lässt sich in der Detailsicht bis zur
            Rechnungsnummer jederzeit nachtragen und ändern. */}
        {vorschlag && !vorschlagUebernommen && (
          <Meldung
            art="neutral"
            titel="Für diese Strecke ist ein Preis hinterlegt"
            knopf={{
              text: 'Übernehmen',
              tun: () => {
                setPreisart(vorschlag.preisart ?? '');
                setPreis(zahlAlsText(vorschlag.preis_vk));
                if (vorschlag.mengeneinheit) setMengeneinheit(vorschlag.mengeneinheit);
              },
            }}
          >
            {vorschlag.preis_vk?.toLocaleString('de-DE', {
              style: 'currency',
              currency: 'EUR',
            })}
            {vorschlag.preisart === 'menge' && vorschlag.mengeneinheit
              ? ` je ${EINHEITTEXT[vorschlag.mengeneinheit] ?? vorschlag.mengeneinheit}`
              : vorschlag.preisart === 'pauschale'
                ? ' pauschale'
                : ' je Stunde'}{' '}
            — aus der Streckenvorlage. Gilt hier etwas anderes, trag es einfach
            von Hand ein.
          </Meldung>
        )}

        <div className="feldreihe">
          <label className="feld">
            <span className="feld__marke">Preisart</span>
            <select
              value={preisart}
              disabled={gesperrt}
              onChange={(e) => setPreisart(e.target.value)}
            >
              <option value="">Steht noch nicht fest</option>
              <option value="pauschale">Pauschale</option>
              <option value="menge">Nach Menge</option>
              <option value="stundensatz">Stundensatz</option>
            </select>
            <span className="feld__hinweis">
              Kann leer bleiben und später nachgetragen werden.
            </span>
          </label>

          <label className="feld">
            <span className="feld__marke">
              {preisart === 'menge'
                ? 'Preis je Einheit (€)'
                : preisart === 'stundensatz'
                  ? 'Stundensatz (€)'
                  : 'Verkaufspreis (€)'}
            </span>
            <Zahlfeld
              wert={preis}
              setzen={setPreis}
              disabled={gesperrt || preisart === ''}
            />
            <span className="feld__hinweis">
              {preisart === 'stundensatz'
                ? 'Die Summe ergibt sich erst aus den Stunden des Fahrers.'
                : 'Netto, ohne Umsatzsteuer.'}
            </span>
          </label>

          {preisart === 'menge' && (
            <label className="feld">
              <span className="feld__marke">Einheit</span>
              <select
                value={mengeneinheit}
                disabled={gesperrt}
                onChange={(e) => setMengeneinheit(e.target.value)}
              >
                <option value="t">Tonne</option>
                <option value="m3">Kubikmeter</option>
                <option value="fuhre">Fuhre</option>
                <option value="stueck">Stück</option>
              </select>
            </label>
          )}
        </div>

        <Textfeld marke="Notiz" wert={notiz} setzen={setNotiz} disabled={gesperrt} />

        <Dateiablage
          key={`dateien-${runde}`}
          dateien={dateien}
          setzen={setDateien}
          arten={DOKUMENTARTEN}
          vorgabeArt="auftragsdokument"
          disabled={gesperrt}
        />

        <div className="reihe reihe--rechts">
          {/* Steht die Tour schon, führt der Hauptknopf nur noch zu ihr. Vorher
              hieß er weiterhin „Tour eintragen" und war anklickbar — und dann
              stand dieselbe Tour zweimal in der Liste. */}
          <Knopf
            art="haupt"
            disabled={!angelegt && (!vollstaendig || laeuft)}
            onClick={() =>
              angelegt ? zurAngelegtenTour(angelegt) : void speichern()
            }
          >
            {angelegt
              ? 'Zur Tour'
              : laeuft
                ? 'Wird eingetragen …'
                : 'Tour eintragen'}
          </Knopf>
        </div>
      </div>
    </Karte>
  );
}
